import React, { useEffect, useReducer } from "react";
import { widthAuthHOC } from "../../hoc";
import { Heading1 } from "../../components/Headings";
import {
  Form,
  Fieldset,
  PasswordInput,
  OTPInput,
  Button,
  MessageBox,
  MobileInput,
} from "../../components/FormElements";
import Loader from "../../components/Loader/Loader";
import {
  StyledAuthLinks,
  StyledLink,
  StyledButtonAsLink,
  StyledAuthFooterLinkText,
  StyledPara,
  StyledTextHighlight,
} from "../../components/styled";
import API from "../../api";
import { setAuthData, getSearchParams } from "../../utils";
import {
  RESPONSE_STATUSES,
  LOGIN_TYPES,
  OTP_VERIFICATION_TYPES,
} from "../../constants";

/* ----------------------
   reducer & state
---------------------- */
const initialState = {
  isLoading: false,
  responseStatus: "",
  responseMessage: "",
  routeStage: "Login", // Login | OTP
  loginType: LOGIN_TYPES.PASSWORD,
  mobileNumber: "",
  password: "",
  requestToken: "",
  oneTimePassword: "",
};

function reducer(state, patch) {
  return { ...state, ...patch };
}

/* ----------------------
   MAIN
---------------------- */
const Login = (props) => {
  const [state, setState] = useReducer(reducer, initialState);

  /* Helpers */
  const clearMessages = () =>
    setState({ responseStatus: "", responseMessage: "" });

  const changeLoginType = () => {
    setState({
      routeStage: "Login",
      password: "",
      requestToken: "",
      oneTimePassword: "",
      loginType:
        state.loginType === LOGIN_TYPES.PASSWORD
          ? LOGIN_TYPES.OTP
          : LOGIN_TYPES.PASSWORD,
    });
    clearMessages();
  };

  const buildPayload = () => {
    if (state.routeStage === "OTP") {
      return {
        requestType: OTP_VERIFICATION_TYPES.LOGIN,
        requestToken: state.requestToken,
        mobileNumber: state.mobileNumber,
        oneTimePassword: state.oneTimePassword,
      };
    }

    if (state.loginType === LOGIN_TYPES.PASSWORD) {
      return {
        loginType: LOGIN_TYPES.PASSWORD,
        mobileNumber: state.mobileNumber,
        password: state.password,
      };
    }

    return {
      loginType: LOGIN_TYPES.OTP,
      mobileNumber: state.mobileNumber,
    };
  };

  /* Submit */
  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ isLoading: true });
    clearMessages();

    try {
      const payload = buildPayload();
      const endpoint =
        state.routeStage === "Login"
          ? "/authentication/login"
          : "/authentication/verify-otp";

      const response = await API.post(endpoint, payload);
      const { status, message, data, token } = response.data;

      if (status === RESPONSE_STATUSES.SUCCESS) {
        setState({
          responseStatus: RESPONSE_STATUSES.SUCCESS,
          responseMessage: message,
        });

        if (state.loginType === LOGIN_TYPES.OTP && state.routeStage === "Login") {
          setState({ requestToken: token, routeStage: "OTP" });
          return;
        }

        setAuthData(token, data);
        return;
      }

      if (status === RESPONSE_STATUSES.TWOFA) {
        setState({ requestToken: token, routeStage: "OTP" });
        return;
      }

      setState({
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: message || "Login failed",
      });
    } catch (error) {
      setState({
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error?.message || "An error occurred",
      });
    } finally {
      setState({ isLoading: false });
    }
  };

  /* Resend OTP */
  const resendOTP = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setState({ isLoading: true });
    clearMessages();

    try {
      const response = await API.post("/authentication/resend-otp", {
        requestToken: state.requestToken,
        mobileNumber: state.mobileNumber,
      });

      const { status, message, token } = response.data;
      setState({
        responseStatus: status,
        responseMessage: message,
        requestToken: token,
      });
    } catch (error) {
      setState({
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error?.message || "Failed to resend OTP",
      });
    } finally {
      setState({ isLoading: false });
    }
  };

  /* On mount */
  useEffect(() => {
    const err = getSearchParams("errorMessage");
    if (err) {
      setState({
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: err,
      });
    }
  }, []);

  /* Text */
  const headingText =
    state.routeStage === "Login" ? "Sign in to your account" : "Verify your OTP";

  const submitText =
    state.routeStage === "Login"
      ? state.loginType === LOGIN_TYPES.PASSWORD
        ? "Sign In"
        : "Send OTP"
      : "Verify & Continue";

  /* ----------------------
      Render
  ---------------------- */
  return (
    <>
      <Loader isLoading={state.isLoading} />

      {/* Glass Card */}
      <div className="auth-glass-card">
        <Heading1 textAlign="center" style={{ marginBottom: 12 }}>
          {headingText}
        </Heading1>

        <Form onSubmit={onSubmit}>
          {/* LOGIN FIELDS */}
          {state.routeStage === "Login" && (
            <>
              <Fieldset>
                <MobileInput
                  value={state.mobileNumber}
                  onChange={(v) => setState({ mobileNumber: v })}
                  placeholder="Mobile Number"
                  disabled={state.isLoading}
                />
              </Fieldset>

              {state.loginType === LOGIN_TYPES.PASSWORD && (
                <Fieldset>
                  <PasswordInput
                    value={state.password}
                    onChange={(v) => setState({ password: v })}
                    placeholder="Password"
                    disabled={state.isLoading}
                  />
                </Fieldset>
              )}
            </>
          )}

          {/* OTP FIELDS */}
          {state.routeStage === "OTP" && (
            <>
              <Fieldset>
                <StyledPara>
                  Enter the OTP sent to{" "}
                  <StyledTextHighlight>{state.mobileNumber}</StyledTextHighlight>
                </StyledPara>
              </Fieldset>

              <Fieldset>
                <OTPInput
                  value={state.oneTimePassword}
                  onChange={(v) => setState({ oneTimePassword: v })}
                  placeholder="Enter OTP"
                />
              </Fieldset>
            </>
          )}

          {/* LINKS */}
          <Fieldset display="block">
            <div className="auth-links-row">
              <div>
                {state.routeStage === "OTP" && (
                  <StyledButtonAsLink type="button" onClick={resendOTP}>
                    Resend OTP
                  </StyledButtonAsLink>
                )}

                {state.routeStage === "Login" &&
                  state.loginType === LOGIN_TYPES.PASSWORD && (
                    <StyledAuthFooterLinkText style={{ marginLeft: 6 }}>
                      <StyledLink to="/forgot-password">
                        Forgot Password?
                      </StyledLink>
                    </StyledAuthFooterLinkText>
                  )}
              </div>

              {state.routeStage !== "OTP" && (
                <StyledButtonAsLink type="button" onClick={changeLoginType}>
                  Login with{" "}
                  {state.loginType === LOGIN_TYPES.PASSWORD
                    ? "OTP"
                    : "Password"}
                </StyledButtonAsLink>
              )}
            </div>
          </Fieldset>

          {/* RESPONSE MESSAGE */}
          {state.responseStatus && state.responseMessage && (
            <Fieldset>
              <MessageBox
                status={state.responseStatus}
                message={state.responseMessage}
              />
            </Fieldset>
          )}

          <Fieldset>
            <Button disabled={state.isLoading}>{submitText}</Button>
          </Fieldset>
        </Form>

        {/* Register Link */}
        {state.routeStage !== "OTP" && (
          <StyledAuthFooterLinkText
            style={{ marginTop: 18, textAlign: "center", display: "block" }}
          >
            New here? <StyledLink to="/register">Create an account</StyledLink>
          </StyledAuthFooterLinkText>
        )}
      </div>

      {/* Styles */}
      <style>
        {`
        .auth-glass-card {
          max-width: 410px;
          margin: 40px auto;
          padding: 34px 26px;
          border-radius: 18px;
          background: rgba(255,255,255,0.14);
          backdrop-filter: blur(14px);
          border: 1px solid rgba(255,255,255,0.32);
          box-shadow: 0 18px 50px rgba(0,0,0,0.10);
          animation: fadeScaleIn 0.35s ease-out;
        }

        .auth-links-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
        }

        @keyframes fadeScaleIn {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}
      </style>
    </>
  );
};

export default widthAuthHOC(Login, "Yes");
